import { createSelector, createSlice } from "@reduxjs/toolkit";
import { createMapSelector, dataHookFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory } from "../../common/reducers/utilities";
import { logout } from "../../common/reducers/initContext";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { ensureArray, groupBy } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = i => i.id;

const inventorySuperSlice = createSlice({
    name: 'superInventory',
    initialState: {
        data: {}
    },
    reducers: {
        setForCompartment: (state, action) => {
            ensureArray(action.payload.compartmentids).forEach(compartmentid => delete state.data[compartmentid]);
            const toSet = ensureArray(action.payload.data);
            const byCompartment = groupBy(toSet, 'compartmentid');
            Object.entries(byCompartment).forEach(([ compartmentid, inventory ]) => {
                state.data[compartmentid] = inventory;
            });
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byCompartment = groupBy(toUpdate, 'compartmentid');
            Object.entries(byCompartment).forEach(([ compartmentid, inventory ]) => {
                const ids = new Set(inventory.map(i => key(i)));
                state.data[compartmentid] = (state.data[compartmentid]?.filter(i => !ids.has(key(i))) ?? []).concat(inventory);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byCompartment = groupBy(toRemove, 'compartmentid');
            Object.entries(byCompartment).forEach(([ compartmentid, inventory ]) => {
                if (state.data[compartmentid]) {
                    const ids = new Set(inventory.map(i => key(i)));
                    state.data[compartmentid] = state.data[compartmentid].filter(i => !ids.has(key(i)));
                    if (state.data[compartmentid].length === 0) {
                        delete state.data[compartmentid];
                    }
                }
            });
        },
        removeForCompartment: (state, action) => {
            ensureArray(action.payload).forEach(c => delete state.data[c.id]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => inventorySuperSlice.getInitialState());
    }
})

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'inventory_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'inventory_one');

const fetchInventoryForCompartment = aggregatingFetch(
    id => network => network.post('/api/v2/superInventoryByCompartment', [id], data => inventorySuperSlice.actions.setForCompartment({ compartmentids: [id], data}), visualizeFailure('get')),
    ids => netowrk => netowrk.post('/api/v2/superInventoryByCompartment', ids, data => inventorySuperSlice.actions.setForCompartment({ compartmentids: ids, data}), visualizeFailure('get')),
    )

export const createInventory = (network, data) => network.post('/api/v1/inventory', data, inventorySuperSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateInventory = (network, data) => network.put('/api/v1/inventory', data, inventorySuperSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeInventory = (network, data) => network.delete(`/api/v1/inventory/${data.id}`, inventorySuperSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectAllInventory = state => state.superInventory.data;
const selectInventoryByCompartment = compartmentid => createSelector([selectAllInventory], inventory => {
    return inventory[compartmentid];
})
const selectInventoryByCompartments = compartmentIds => createMapSelector([selectAllInventory], inventory => {
    return compartmentIds.reduce((acc, id) => {
        if (inventory[id]) {
            acc[id] = inventory[id];
        }
        return acc;
    }, {});
});

// #endregion

const listen = {
    inventoryCreated: (inventory, { dispatch, websocket }) => {
        markFetched(websocket, 'inventory', key(inventory))
        dispatch(inventorySuperSlice.actions.addOrUpdate(inventory))
    },
    inventoryUpdated: (inventory, { dispatch }) => dispatch(inventorySuperSlice.actions.addOrUpdate(inventory)),
    inventoryRemoved: (inventory, { dispatch }) => dispatch(inventorySuperSlice.actions.remove(inventory)),
    compartmentRemoved: (compartment, { dispatch }) => dispatch(inventorySuperSlice.actions.removeForCompartment(compartment))
}

const inventoryInitByID = initMapFactory(fetchInventoryForCompartment, listen);
registerEntityBasedFetch('compartment', inventoryInitByID)
const cleanup = { root: inventorySuperSlice.name, all: state => Object.values(selectAllInventory(state)).flat(), removeAll: inventorySuperSlice.actions.remove, key };

export const [useSuperInventory] = dataHookFactory(selectInventoryByCompartment, initMultiMapFactory(inventoryInitByID, listen), {cleanup});
export const [useSuperInventories] = dataHookFactory(selectInventoryByCompartments, initMultiMapFactory(inventoryInitByID, listen), {cleanup});


const reducer = { name: inventorySuperSlice.name, reducer: inventorySuperSlice.reducer }
export default reducer;